import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, useForm, useFormState } from 'react-final-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import { Block, Button, FormCustomSelect, FormField, FormPillSelector, Input } from 'components';
import { actions, duck } from 'coupon-common/src/modules/locations';
import {
    composeSyncValidators,
    getNormalizedEntity,
    maxLength,
    required,
} from 'coupon-common/src/utils';

import messages from './messages';

const AdditionalLocationsFieldGroup = ({ dispatch, locations, intl, readOnly = false }) => {
    const form = useForm();
    const [selectedProvinces, setSelectedProvinces] = useState([]);
    const [additionalLocations, setAdditionalLocations] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);
    const provincesKey = duck.provincesEntity.key;

    useEffect(() => {
        dispatch(actions.getProvinces());
        dispatch(actions.getCities());
    }, [dispatch]);

    const {
        entityList: provinceOptions,
        isFetching: provincesFetching,
        error: provincesLoadingError,
    } = getNormalizedEntity(
        locations,
        provincesKey,
        intl,
        messages.entityProvinces || { defaultMessage: 'Provinces' }
    );

    const cityOptions = Object.values(locations?.entities?.cities || {});

    const {
        values: { additionalProvinces, additional_cities = [], searchCities = '' } = {},
    } = useFormState({
        subscription: { values: true },
    });

    // Filter cityOptions based on province and search string
    const filteredCityOptions = useMemo(() => {
        if (!additionalProvinces?.id) return [];

        const provinceCities = cityOptions.filter(city => city.province === additionalProvinces.id);

        if (!searchCities) return provinceCities;

        const lowerSearch = searchCities.toLowerCase();

        return provinceCities.filter(city =>
            city.name?.toLowerCase().includes(lowerSearch)
        );
    }, [additionalProvinces, cityOptions, searchCities]);

    useEffect(() => {

        if (!additional_cities.length || !provinceOptions.length) return;

        const uniqueProvinceIds = [...new Set(additional_cities.map(city => city.province))];
        const preselectedProvinces = provinceOptions.filter(prov =>
            uniqueProvinceIds.includes(prov.id)
        );

        if (preselectedProvinces.length) {
            setSelectedProvinces(preselectedProvinces);
            if (!filteredCityOptions.length) {
                form.change('additionalProvinces', preselectedProvinces[0]);
            }
        }
    }, [additional_cities, readOnly]);

    const handleProvinceClick = (prov) => {
        const filteredCities = additional_cities?.filter(
            (city) => city?.province !== prov?.id
        );

        // 🟩 Get the province of the first remaining city, if any
        const remainingProvinceId = filteredCities?.[0]?.province;
        const currentProvince = provinceOptions.find(
            (p) => p.id === remainingProvinceId
        );

        // If no cities remain, clear the province selection
        form.change('additionalProvinces', currentProvince || undefined);
        form.change('additional_cities', filteredCities);
    };

    return (
        <Block>
            {/* <div className='d-flex align-items-center'>
                <input
                    type="checkbox"
                    className="mr-2"
                    checked={additionalLocations}
                    onChange={(e) => handleCheckbox(e)}
                />
                <label className='text-muted'>
                    {intl.formatMessage(messages.additionalLocationsInstructions)}
                </label>
            </div> */}

            {/* {additionalLocations &&
                ( */}
            <div>
                <h5>
                    <FormattedMessage {...messages.blockHeading} />
                </h5>
                <p className="text-muted">
                    {intl.formatMessage(messages.blockHeadingInstruction)}
                </p>
                <Field
                    name="additionalProvinces"
                    label={intl.formatMessage(messages.provinceFieldLabel || { defaultMessage: 'Province' })}
                    placeholder={intl.formatMessage(messages.provinceFieldPlaceholder || { defaultMessage: 'Select a province' })}
                    component={FormCustomSelect}
                    fetching={provincesFetching}
                    options={provinceOptions}
                    externalError={provincesLoadingError}
                    readOnly={readOnly}
                />
                {additionalProvinces && (


                    <div>
                        <p className="text-muted m-0">
                            Selected Provinces
                        </p>

                        <div className='d-flex flex-wrap'
                            style={{
                                maxHeight: '10rem',
                                overflowY: 'auto',
                                border: 'none',
                                padding: '5px',
                            }}>
                            {
                                selectedProvinces?.map((prov) => {
                                    return (
                                        <div key={prov?.id}>
                                            <Button
                                                checkable
                                                checked
                                                disabled={readOnly}
                                                type="button"
                                                onClick={() => handleProvinceClick(prov)}
                                            >
                                                {`${prov?.name} x`}
                                            </Button>
                                        </div>
                                    )
                                })
                            }
                        </div>
                    </div>
                )}

                {additionalProvinces && (
                    <>
                        <h6>{intl.formatMessage(messages.cityBlockHeading || { defaultMessage: 'Select Cities' })}</h6>
                        <p className="text-muted">
                            {intl.formatMessage(messages.cityInstruction || { defaultMessage: 'Choose one or more cities.' })}
                        </p>
                        <Field
                            name="searchCities"
                            placeholder={'Search for a city'}
                            component={FormField}
                            readOnly={readOnly}
                        />
                        <div
                            style={{
                                maxHeight: '15rem',
                                overflowY: 'auto',
                                border: 'none',
                                padding: '5px',
                            }}
                        >
                            <Field
                                name="additional_cities"
                                type="select"
                                component={FormPillSelector}
                                options={filteredCityOptions}
                                showSelectAll
                                readOnly={readOnly}
                            />
                        </div>
                    </>
                )}
            </div>
            {/* )} */}

        </Block>
    );
};

AdditionalLocationsFieldGroup.propTypes = {
    intl: PropTypes.object,
    dispatch: PropTypes.func,
    locations: PropTypes.object,
    businessInfo: PropTypes.object,
};

const mapStateToProps = (state) => ({
    locations: state.locations,
    businessInfo: state.businessInfo.data,
});

export default connect(mapStateToProps)(injectIntl(AdditionalLocationsFieldGroup));
